import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { FunctionComponent, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FilterControlsLayout } from '../../components/Accessibility';
import { EditQuestionData, EditQuestionDialog } from '../../components/admin/EditQuestionDialog';
import { FilterControls } from '../../components/admin/FilterControls';
import { useLoadingState } from '../../components/admin/Loading';
import { QuestionFiltersProvider, useQuestionFilters } from '../../components/admin/QuestionFilterContext';
import { QuestionSelectionList } from '../../components/admin/QuestionsList';
import { WideAdminLayout } from '../../components/layout/AdminLayout';
import { useAuthenticatedUser } from '../../contexts/AuthContext';
import { withErrorAlerts } from '../../data/Errors';
import { QuestionOperations, QuestionSetOperations } from '../../data/Firestore';
import { QuestionConfig, QuestionId } from '../../types/GameplayTypes';
import { getSharedQuestionId } from '../../util/Sharing';
import { pluralize } from '../../util/Text';

export const QuestionsScreen: FunctionComponent = () =>
	<QuestionFiltersProvider>
		<QuestionsScreenContent />
	</QuestionFiltersProvider>;

const QuestionsScreenContent: FunctionComponent = () => {
	const [questions, setQuestions] = useState<QuestionConfig[]>([]);
	const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set() });
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editingQuestion, setEditingQuestion] = useState<QuestionConfig>();

	const { applyFilters } = useQuestionFilters();
	const { user } = useAuthenticatedUser();
	const [searchParams] = useSearchParams();

	const { load: loadQuestions, loading, error } = useLoadingState({
		message: 'Loading questions...', failureMessage: 'Failed to load questions'
	}, async function () {
		const data = await QuestionOperations.getAll();
		setQuestions(data);

		const questionIdToOpen = getSharedQuestionId(searchParams);
		if (questionIdToOpen) {
			const questionToEdit = data.find(q => q.id === questionIdToOpen);
			if (questionToEdit) {
				setEditingQuestion(questionToEdit);
				setDialogOpen(true);
			}
		}
	});

	useEffect(function initialLoad() {
		void loadQuestions();
	}, [loadQuestions]);

	function handleCreateNew() {
		setEditingQuestion(undefined);
		setDialogOpen(true);
	}

	function handleEdit(question: QuestionConfig) {
		setEditingQuestion(question);
		setDialogOpen(true);
	}

	async function handleDelete(id: QuestionId) {
		const setCount = await QuestionSetOperations.countSetsContainingQuestions([id]);
		let message = 'Are you sure you want to delete this question?';
		if (setCount > 0) {
			message += ` It will be removed from ${pluralize(setCount, 'Question Set', { quantityPrefix: true })}.`;
		}
		if (confirm(message)) {
			await withErrorAlerts('Failed to delete question', async () => {
				await QuestionSetOperations.removeQuestionsFromAll([id]);
				await QuestionOperations.delete(id);
				await loadQuestions();
			});
		}
	}

	async function handleBulkDelete() {
		const selectedIds = Array.from(selectionModel.ids) as QuestionId[];
		if (!selectedIds.empty()) {
			const setCount = await QuestionSetOperations.countSetsContainingQuestions(selectedIds);
			let message = `Delete ${pluralize(selectedIds.length, 'question', { quantityPrefix: true })}?`;
			if (setCount > 0) {
				message += ` They will be removed from ${pluralize(setCount, 'Question Set', { quantityPrefix: true })}.`;
			}

			if (confirm(message)) {
				await withErrorAlerts('Failed to delete questions', async () => {
					await QuestionSetOperations.removeQuestionsFromAll(selectedIds);
					await Promise.all(
						selectedIds.map(id => QuestionOperations.delete(id))
					);
					setSelectionModel({ type: 'include', ids: new Set() });
					await loadQuestions();
				});
			}
		}
	}

	async function handleSaveQuestion(formData: EditQuestionData) {
		await withErrorAlerts('Failed to save question', async () => {
			if (editingQuestion) {
				await QuestionOperations.update(editingQuestion.id, formData);
			} else {
				await QuestionOperations.create({
					...formData,
					authorName: user.displayName ?? 'Unknown',
				});
			}
			setDialogOpen(false);
			await loadQuestions();
		});
	}

	// Get unique tags and authors for filters
	const allTags = questions.flatMap(q => q.tags).uniqueValues().sort();
	const allAuthors = questions.map(q => q.authorName).uniqueValues().sort();
	const filteredQuestions = applyFilters(questions);

	return <WideAdminLayout title='Questions' loading={loading} error={error} width='xl'>
		<FilterControlsLayout
			Controls={<FilterControls allTags={allTags} allAuthors={allAuthors} />}
			Buttons={<>
				{(selectionModel.ids.size > 0) &&
					<Button
						variant="outlined"
						color="error"
						startIcon={<DeleteIcon />}
						onClick={handleBulkDelete}
					>
						Delete {selectionModel.ids.size} selected
					</Button>}
				<Button
					variant="contained"
					startIcon={<AddIcon />}
					onClick={handleCreateNew}
				>
					Create Question
				</Button>
			</>} />

		<QuestionSelectionList
			questions={filteredQuestions}
			selectionModel={selectionModel}
			onSelectionChange={setSelectionModel}
			onEdit={handleEdit}
			onDelete={handleDelete}
		/>

		<EditQuestionDialog
			open={dialogOpen}
			question={editingQuestion}
			onClose={() => setDialogOpen(false)}
			onSave={handleSaveQuestion}
		/>
	</WideAdminLayout>;
};
